import React, { useState, useEffect } from 'react';
import { useMarket } from '../context/MarketContext';

export default function QuickOrderPanel() {
  const { 
    currentPrice, 
    orderBook, 
    selectedSymbol, 
    placeOrder,
    connectionStatus 
  } = useMarket();
  
  const [selectedSide, setSelectedSide] = useState<'BUY' | 'SELL' | null>(null);
  const [quantity, setQuantity] = useState<string>('');
  const [price, setPrice] = useState<string>('');
  const [orderType, setOrderType] = useState<'LIMIT' | 'MARKET'>('LIMIT');

  // Get best bid and ask prices
  const bestBid = orderBook.bids.length > 0 ? orderBook.bids[0].price : null;
  const bestAsk = orderBook.asks.length > 0 ? orderBook.asks[0].price : null;

  // Update price placeholder when side is selected
  useEffect(() => {
    if (selectedSide === 'BUY' && bestBid) {
      setPrice(bestBid.toFixed(2));
    } else if (selectedSide === 'SELL' && bestAsk) {
      setPrice(bestAsk.toFixed(2));
    } else if (selectedSide && currentPrice > 0) {
      setPrice(currentPrice.toFixed(2));
    }
  }, [selectedSide, bestBid, bestAsk, currentPrice]);

  // Reset quantity placeholder when side is selected
  useEffect(() => {
    if (selectedSide && !quantity) {
      setQuantity('1000');
    }
  }, [selectedSide, quantity]);

  const handlePlaceOrder = () => {
    if (!selectedSide) {
      alert('Please select BUY or SELL first');
      return;
    }

    const qty = parseFloat(quantity);
    if (!qty || qty <= 0) {
      alert('Please enter a valid quantity');
      return;
    }

    if (orderType === 'LIMIT') {
      const px = parseFloat(price);
      if (!px || px <= 0) {
        alert('Please enter a valid price for limit orders');
        return;
      }
    }

    if (connectionStatus !== 'connected') {
      alert('Cannot place order - WebSocket not connected');
      return;
    }

    // Generate client order ID
    const clientOrderId = `ORD_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    placeOrder({
      clientOrderId,
      symbol: selectedSymbol,
      side: selectedSide,
      orderType: orderType,
      price: orderType === 'LIMIT' ? parseFloat(price) : undefined,
      quantity: qty
    });

    // Reset form
    setSelectedSide(null);
    setQuantity('');
    setPrice('');
    setOrderType('LIMIT');
  };

  const isConnected = connectionStatus === 'connected';

  return (
    <div style={{ background:'#1e293b', padding:20, borderRadius:8, border:'1px solid #334155' }}>
      <h3 style={{ margin:'0 0 16px 0', fontSize:16, fontWeight:600 }}>Quick Order</h3>
      
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
        <button 
          onClick={() => setSelectedSide('BUY')}
          style={{
            padding:12, 
            background: selectedSide === 'BUY' ? '#10b981' : '#334155',
            color:'white', 
            border: selectedSide === 'BUY' ? '3px solid #34d399' : '1px solid #475569',
            borderRadius:6, 
            cursor:'pointer', 
            fontSize:14, 
            fontWeight:600,
            transition: 'all 0.2s',
            opacity: isConnected ? 1 : 0.5,
            boxShadow: selectedSide === 'BUY' ? '0 0 0 2px rgba(16, 185, 129, 0.3)' : 'none'
          }}
          disabled={!isConnected}
        >
          BUY
        </button>
        <button 
          onClick={() => setSelectedSide('SELL')}
          style={{
            padding:12, 
            background: selectedSide === 'SELL' ? '#ef4444' : '#334155',
            color:'white', 
            border: selectedSide === 'SELL' ? '3px solid #f87171' : '1px solid #475569',
            borderRadius:6, 
            cursor:'pointer', 
            fontSize:14, 
            fontWeight:600,
            transition: 'all 0.2s',
            opacity: isConnected ? 1 : 0.5,
            boxShadow: selectedSide === 'SELL' ? '0 0 0 2px rgba(239, 68, 68, 0.3)' : 'none'
          }}
          disabled={!isConnected}
        >
          SELL
        </button>
      </div>

      {!isConnected && (
        <div style={{ 
          padding:8, 
          background:'#7f1d1d', 
          color:'#fca5a5', 
          borderRadius:4, 
          fontSize:11, 
          marginBottom:12,
          textAlign:'center'
        }}>
          ⚠️ Not connected - orders disabled
        </div>
      )}

      {selectedSide && (
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          <div>
            <label style={{ fontSize:12, color:'#94a3b8', marginBottom:4, display:'block' }}>Order Type</label>
            <div style={{ display:'flex', gap:8 }}>
              <button
                onClick={() => setOrderType('LIMIT')}
                style={{
                  flex: 1,
                  padding:8, 
                  background: orderType === 'LIMIT' ? '#3b82f6' : '#334155',
                  color:'white', 
                  border: orderType === 'LIMIT' ? '2px solid #3b82f6' : '1px solid #475569',
                  borderRadius:4, 
                  cursor:'pointer', 
                  fontSize:12, 
                  fontWeight:500
                }}
              >
                LIMIT
              </button>
              <button
                onClick={() => setOrderType('MARKET')}
                style={{
                  flex: 1,
                  padding:8, 
                  background: orderType === 'MARKET' ? '#3b82f6' : '#334155',
                  color:'white', 
                  border: orderType === 'MARKET' ? '2px solid #3b82f6' : '1px solid #475569',
                  borderRadius:4, 
                  cursor:'pointer', 
                  fontSize:12, 
                  fontWeight:500
                }}
              >
                MARKET
              </button>
            </div>
          </div>

          <div>
            <label style={{ fontSize:12, color:'#94a3b8', marginBottom:4, display:'block' }}>Quantity</label>
            <input 
              type="number" 
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="1000"
              style={{
                width:'100%', 
                padding:'8px 12px', 
                background:'#334155', 
                color:'#e2e8f0',
                border:'1px solid #475569', 
                borderRadius:6, 
                fontSize:13,
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          {orderType === 'LIMIT' && (
            <div>
              <label style={{ fontSize:12, color:'#94a3b8', marginBottom:4, display:'block' }}>Price</label>
              <input 
                type="number" 
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder={
                  selectedSide === 'BUY' 
                    ? (bestBid ? bestBid.toFixed(2) : currentPrice.toFixed(2))
                    : (bestAsk ? bestAsk.toFixed(2) : currentPrice.toFixed(2))
                }
                style={{
                  width:'100%', 
                  padding:'8px 12px', 
                  background:'#334155', 
                  color:'#e2e8f0',
                  border:'1px solid #475569', 
                  borderRadius:6, 
                  fontSize:13,
                  boxSizing: 'border-box'
                }}
              />
              <div style={{ fontSize:10, color:'#64748b', marginTop:4 }}>
                {selectedSide === 'BUY' && bestBid && `Best Bid: ${bestBid.toFixed(2)}`}
                {selectedSide === 'SELL' && bestAsk && `Best Ask: ${bestAsk.toFixed(2)}`}
              </div>
            </div>
          )}

          <button 
            onClick={handlePlaceOrder}
            disabled={!isConnected || !selectedSide}
            style={{
              padding:10, 
              background: isConnected && selectedSide ? '#3b82f6' : '#475569',
              color:'white', 
              border:'none',
              borderRadius:6, 
              cursor: isConnected && selectedSide ? 'pointer' : 'not-allowed',
              fontSize:13, 
              fontWeight:500, 
              marginTop:8,
              opacity: isConnected && selectedSide ? 1 : 0.5
            }}
          >
            Place {selectedSide} Order
          </button>
        </div>
      )}

      {!selectedSide && (
        <div style={{ 
          padding:12, 
          background:'#334155', 
          borderRadius:6, 
          fontSize:12, 
          color:'#94a3b8',
          textAlign:'center'
        }}>
          Select BUY or SELL to place an order
        </div>
      )}
    </div>
  );
}